import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, GitPullRequest, Zap, MessageSquare, UserPlus, Clock, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import { RefreshButton } from './RefreshButton';
interface ActivityFeedProps {
  isLive?: boolean;
  isLoading?: boolean;
}
interface ActivityEvent {
  id: string;
  type: 'issue_done' | 'blocker' | 'pr_merged' | 'sprint_update' | 'comment' | 'member_joined' | 'deploy';
  title: string;
  description: string;
  user: string;
  initials: string;
  time: string;
  timeAgo: string;
  isNew?: boolean;
}
const initialEvents: ActivityEvent[] = [{
  id: 'a1',
  type: 'issue_done',
  title: 'VSM-104 completado',
  description: 'Configurar pipeline CI/CD marcado como Done',
  user: 'Carlos Ruiz',
  initials: 'CR',
  time: '07:52',
  timeAgo: 'hace 3 min',
  isNew: true
}, {
  id: 'a2',
  type: 'blocker',
  title: 'Bloqueador detectado',
  description: 'VSM-101 sin avance por 2 días — posible impedimento',
  user: 'Agent',
  initials: 'AI',
  time: '07:48',
  timeAgo: 'hace 7 min',
  isNew: true
}, {
  id: 'a3',
  type: 'pr_merged',
  title: 'PR #47 mergeado',
  description: 'feat: dashboard KPIs base implementation',
  user: 'Sofia Chen',
  initials: 'SC',
  time: '07:30',
  timeAgo: 'hace 25 min'
}, {
  id: 'a4',
  type: 'sprint_update',
  title: 'Sprint al 65%',
  description: 'Sprint 24 superó el punto medio — buen ritmo',
  user: 'Agent',
  initials: 'AI',
  time: '07:00',
  timeAgo: 'hace 55 min'
}, {
  id: 'a5',
  type: 'comment',
  title: 'Comentario en VSM-102',
  description: '"Necesitamos revisar los colores del tema oscuro"',
  user: 'Ana García',
  initials: 'AG',
  time: '06:45',
  timeAgo: 'hace 1h'
}, {
  id: 'a6',
  type: 'deploy',
  title: 'Deploy a staging',
  description: 'v0.8.2 desplegado exitosamente en us-east-1',
  user: 'Carlos Ruiz',
  initials: 'CR',
  time: '06:20',
  timeAgo: 'hace 1.5h'
}, {
  id: 'a7',
  type: 'issue_done',
  title: 'VSM-103 en revisión',
  description: 'Error en cálculo de velocidad movido a Review',
  user: 'Miguel Silva',
  initials: 'MS',
  time: '05:50',
  timeAgo: 'hace 2h'
}];
// Simulated new events that arrive over time
const newEventPool: ActivityEvent[] = [{
  id: 'n1',
  type: 'comment',
  title: 'Nuevo comentario',
  description: '"¿Podemos hacer el daily a las 10?',
  user: 'Laura Park',
  initials: 'LP',
  time: '',
  timeAgo: 'ahora',
  isNew: true
}, {
  id: 'n2',
  type: 'pr_merged',
  title: 'PR #48 abierto',
  description: 'fix: velocity calculation edge case',
  user: 'Miguel Silva',
  initials: 'MS',
  time: '',
  timeAgo: 'ahora',
  isNew: true
}, {
  id: 'n3',
  type: 'issue_done',
  title: 'VSM-106 actualizado',
  description: 'Optimizar consultas DB — estimación ajustada a 8pts',
  user: 'Miguel Silva',
  initials: 'MS',
  time: '',
  timeAgo: 'ahora',
  isNew: true
}];
const eventConfig = {
  issue_done: {
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/40 border-emerald-900/40'
  },
  blocker: {
    icon: AlertTriangle,
    color: 'text-red-400',
    bg: 'bg-red-950/40 border-red-900/40'
  },
  pr_merged: {
    icon: GitPullRequest,
    color: 'text-violet-400',
    bg: 'bg-violet-950/40 border-violet-900/40'
  },
  sprint_update: {
    icon: Zap,
    color: 'text-indigo-400',
    bg: 'bg-indigo-950/40 border-indigo-900/40'
  },
  comment: {
    icon: MessageSquare,
    color: 'text-blue-400',
    bg: 'bg-blue-950/40 border-blue-900/40'
  },
  member_joined: {
    icon: UserPlus,
    color: 'text-amber-400',
    bg: 'bg-amber-950/40 border-amber-900/40'
  },
  deploy: {
    icon: Zap,
    color: 'text-cyan-400',
    bg: 'bg-cyan-950/40 border-cyan-900/40'
  }
};
export function ActivityFeed({
  isLive = false,
  isLoading = false
}: ActivityFeedProps) {
  const [events, setEvents] = useState<ActivityEvent[]>(initialEvents);
  const [newCount, setNewCount] = useState(2);
  const [poolIndex, setPoolIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Close fullscreen on Escape
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);
  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  // Simulate live events arriving (only in mock mode)
  useEffect(() => {
    if (isLive) return;
    const interval = setInterval(() => {
      if (poolIndex < newEventPool.length) {
        const newEvent = {
          ...newEventPool[poolIndex],
          id: `live-${Date.now()}`,
          time: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })
        };
        setEvents(prev => [newEvent, ...prev.slice(0, 9)]);
        setNewCount(prev => prev + 1);
        setPoolIndex(prev => prev + 1);
      }
    }, 18000);
    return () => clearInterval(interval);
  }, [poolIndex, isLive]);
  const markAllRead = () => setNewCount(0);
  // Live mode: show placeholder until real activity integration
  if (isLive) {
    return <div className="flex flex-col h-full" data-id="element-1723">
        <div className="flex items-center justify-between mb-4" data-id="element-1724">
          <h3 className="font-bold text-slate-100 text-sm" data-id="element-1725">Activity Feed</h3>
          <div className="flex items-center gap-1" data-id="element-1726">
            <span className="text-[10px] text-emerald-400 bg-emerald-950/40 border border-emerald-900/40 px-1.5 py-0.5 rounded-full" data-id="element-1727">
              Live
            </span>
            <button onClick={() => setIsFullscreen(true)} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all" title="Pantalla completa" data-id="element-1728">
              <Maximize2 size={14} data-id="element-1729" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4" data-id="element-1730">
          <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-3" data-id="element-1731">
            <Zap size={18} className="text-slate-600" data-id="element-1732" />
          </div>
          <p className="text-xs text-slate-500 leading-relaxed" data-id="element-1733">
            Activity feed en modo Live próximamente. Los eventos se mostrarán
            desde Jira en tiempo real.
          </p>
        </div>
      </div>;
  }
  if (isLoading) {
    return <div className="flex flex-col h-full" data-id="element-1734">
        <div className="flex items-center justify-between mb-4 animate-pulse" data-id="element-1735">
          <div className="h-5 bg-slate-800 rounded w-24" data-id="element-1736"></div>
          <div className="h-5 bg-slate-800 rounded w-16" data-id="element-1737"></div>
        </div>
        <div className="space-y-3 flex-1" data-id="element-1738">
          {[1, 2, 3, 4].map(i => <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-slate-800/50 bg-slate-800/20 animate-pulse" data-id="element-1739">
              <div className="w-6 h-6 rounded-lg bg-slate-800 shrink-0" data-id="element-1740"></div>
              <div className="flex-1 space-y-2" data-id="element-1741">
                <div className="flex justify-between" data-id="element-1742">
                  <div className="h-3 bg-slate-800 rounded w-1/2" data-id="element-1743"></div>
                  <div className="h-2 bg-slate-800 rounded w-8" data-id="element-1744"></div>
                </div>
                <div className="h-2 bg-slate-800 rounded w-3/4" data-id="element-1745"></div>
                <div className="h-2 bg-slate-800 rounded w-1/4" data-id="element-1746"></div>
              </div>
            </div>)}
        </div>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-1747">
      <div className="flex items-center justify-between mb-4" data-id="element-1748">
        <div className="flex items-center gap-2" data-id="element-1749">
          <h3 className="font-bold text-slate-100 text-sm" data-id="element-1750">Activity Feed</h3>
          {newCount > 0 && <span className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] font-bold rounded-full" data-id="element-1751">
              {newCount}
            </span>}
        </div>
        <div className="flex items-center gap-1" data-id="element-1752">
          <RefreshButton onRefresh={() => {
          setNewCount(0);
        }} data-id="element-1753" />

          <button onClick={markAllRead} className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1" data-id="element-1754">
            <RefreshCw size={10} data-id="element-1755" /> Marcar leídos
          </button>
          <button onClick={() => setIsFullscreen(true)} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-300 hover:bg-slate-800 transition-all" title="Pantalla completa" data-id="element-1756">
            <Maximize2 size={14} data-id="element-1757" />
          </button>
        </div>
      </div>

      <div className="space-y-2 overflow-y-auto flex-1 pr-1" data-id="element-1758">
        {events.map((event, idx) => {
        const cfg = eventConfig[event.type];
        const Icon = cfg.icon;
        return <div key={event.id} className={`flex items-start gap-3 p-2.5 rounded-lg border transition-all ${event.isNew && idx < 2 ? cfg.bg : 'bg-slate-800/30 border-slate-800/50'}`} data-id="element-1759">
              <div className={`p-1.5 rounded-lg shrink-0 ${event.isNew && idx < 2 ? '' : 'bg-slate-800'}`} data-id="element-1760">
                <Icon size={12} className={cfg.color} data-id="element-1761" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1762">
                <div className="flex items-center justify-between gap-2" data-id="element-1763">
                  <p className="text-xs font-semibold text-slate-200 truncate" data-id="element-1764">
                    {event.title}
                  </p>
                  <span className="text-[10px] text-slate-600 shrink-0 flex items-center gap-0.5" data-id="element-1765">
                    <Clock size={9} data-id="element-1766" /> {event.timeAgo}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5 truncate" data-id="element-1767">
                  {event.description}
                </p>
                <p className="text-[10px] text-slate-600 mt-0.5" data-id="element-1768">
                  {event.user}
                </p>
              </div>
            </div>;
      })}
      </div>

      {/* Fullscreen Overlay */}
      {isFullscreen && <div className="fixed inset-0 z-[100] flex flex-col animate-in fade-in duration-200" data-id="element-1769">
          <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-sm" onClick={() => setIsFullscreen(false)} data-id="element-1770" />

          <div className="relative z-10 flex flex-col h-full m-4 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" data-id="element-1771">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900 shrink-0" data-id="element-1772">
              <div className="flex items-center gap-3" data-id="element-1773">
                <h2 className="text-lg font-bold text-slate-100" data-id="element-1774">
                  Activity Feed
                </h2>
                {newCount > 0 && <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs font-bold rounded-full" data-id="element-1775">
                    {newCount} nuevos
                  </span>}
              </div>
              <div className="flex items-center gap-2" data-id="element-1776">
                <button onClick={markAllRead} className="text-xs text-slate-400 hover:text-slate-200 transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-800" data-id="element-1777">
                  <RefreshCw size={12} data-id="element-1778" /> Marcar leídos
                </button>
                <button onClick={() => setIsFullscreen(false)} className="p-2 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-all" title="Cerrar (Esc)" data-id="element-1779">
                  <Minimize2 size={18} data-id="element-1780" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6" data-id="element-1781">
              <div className="max-w-2xl mx-auto space-y-3" data-id="element-1782">
                {events.map((event, idx) => {
              const cfg = eventConfig[event.type];
              const Icon = cfg.icon;
              return <div key={event.id} className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${event.isNew && idx < 2 ? cfg.bg : 'bg-slate-800/30 border-slate-800/50'}`} data-id="element-1783">
                      <div className={`p-2 rounded-lg shrink-0 ${event.isNew && idx < 2 ? '' : 'bg-slate-800'}`} data-id="element-1784">
                        <Icon size={16} className={cfg.color} data-id="element-1785" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1786">
                        <div className="flex items-center justify-between gap-2" data-id="element-1787">
                          <p className="text-sm font-semibold text-slate-200" data-id="element-1788">
                            {event.title}
                          </p>
                          <span className="text-xs text-slate-500 shrink-0 flex items-center gap-1" data-id="element-1789">
                            <Clock size={11} data-id="element-1790" /> {event.timeAgo}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1" data-id="element-1791">
                          {event.description}
                        </p>
                        <p className="text-xs text-slate-500 mt-1" data-id="element-1792">
                          {event.user}
                        </p>
                      </div>
                    </div>;
            })}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}